import { create, type StoreApi, type UseBoundStore } from "zustand";
import { getSocket } from "@/lib/socketClient";
import { onResync } from "@/lib/socketRecovery";
import { listNotificationsApi, markAllNotificationsReadApi, markNotificationReadApi } from "@/portals/patient/api/notificationsApi";
import { usePatientAuthStore } from "@/stores/patientAuthStore";

export type NotificationType =
  | "CAREGIVER_INVITE_RECEIVED"
  | "CAREGIVER_INVITE_ACCEPTED"
  | "CAREGIVER_INVITE_DECLINED"
  | "CAREGIVER_INVITE_CANCELLED"
  | "ACCESS_REQUEST_RECEIVED"
  | "ACCESS_REQUEST_APPROVED"
  | "ACCESS_REQUEST_DECLINED"
  | "CAREGIVER_ACCESS_REVOKED"
  | "CAREGIVER_ACCESS_UPDATED"
  | "ACCESS_CATEGORY_REQUESTED"
  | "ACCESS_CATEGORY_APPROVED"
  | "ACCESS_CATEGORY_DECLINED"
  | "CAREGIVER_ACCESS_EXPIRING_SOON"
  | "REVIEW_ITEM_NEEDS_INPUT"
  // Audience PATIENT (Module 5).
  | "REVIEW_ITEM_QUESTION"
  | "REVIEW_ITEM_RESOLVED"
  // Audience ADMIN (Module 6).
  | "UNCLAIMED_ITEMS_ALERT"
  | "PHYSICIAN_INVITE_DECLINED"
  // Audience PHYSICIAN (Module 4) — only ever served by /api/physician/notifications.
  | "REVIEW_ITEM_ASSIGNED"
  | "REVIEW_ITEM_PATIENT_REPLIED";

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  data: {
    relationshipId?: string;
    categoryRequestId?: string;
    /** REVIEW_ITEM_NEEDS_INPUT: whose medications need input (self or an own dependent). */
    profileId?: string;
    count?: number;
    /** REVIEW_ITEM_ASSIGNED / _PATIENT_REPLIED (physician) and REVIEW_ITEM_QUESTION / _RESOLVED (patient). */
    reviewItemId?: string;
    /** UNCLAIMED_ITEMS_ALERT (admin audience). */
    reviewItemIds?: string[];
    /** PHYSICIAN_INVITE_DECLINED (admin audience). */
    invitationId?: string;
  } | null;
  isRead: boolean;
  createdAt: string;
}

export interface NotificationsState {
  notifications: AppNotification[];
  unreadCount: number;
  isLoading: boolean;
  fetch: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  /** Drops a notification from the list entirely — used once its inline
   * Approve/Deny action has resolved it, so it doesn't linger looking
   * actionable when it no longer is. */
  remove: (id: string) => void;
}

type ListResult = { data: { data: AppNotification[]; unreadCount: number } | null };

/** Everything that differs between one portal's notification feed and another's. */
export interface NotificationsStoreConfig {
  list: () => Promise<ListResult>;
  markRead: (id: string) => Promise<unknown>;
  markAllRead: () => Promise<unknown>;
  /** Registers the realtime "notification:new" listener for this portal's socket. */
  onIncoming: (handler: (incoming: AppNotification) => void) => void;
  /** Calls back with the portal's access token whenever it changes (null on logout). */
  onTokenChange: (handler: (token: string | null) => void) => void;
  /** Calls back when the portal's socket may have missed events (reconnect / server resync). */
  onResync?: (handler: () => void) => void;
}

export type NotificationsStore = UseBoundStore<StoreApi<NotificationsState>>;

/**
 * One portal's notification feed. Global, un-scoped like the auth stores (not a
 * React Context) — a portal's bell needs the same live state wherever its header
 * mounts it. Each portal gets its own instance (patient below; physician in
 * physicianNotificationsStore.ts), so their feeds can never mix.
 */
export function createNotificationsStore(config: NotificationsStoreConfig): NotificationsStore {
  return create<NotificationsState>((set, get) => {
    config.onIncoming((incoming) => {
      set((state) => {
        // A refetch can already have brought this one in — never list it twice.
        if (state.notifications.some((n) => n.id === incoming.id)) return state;
        return {
          notifications: [incoming, ...state.notifications],
          unreadCount: state.unreadCount + (incoming.isRead ? 0 : 1),
        };
      });
    });

    let lastToken: string | null = null;
    // Events sent while the socket was down are never replayed: refetch the list.
    config.onResync?.(() => {
      if (lastToken) void get().fetch();
    });
    config.onTokenChange((token) => {
      if (token === lastToken) return;
      lastToken = token;
      if (token) {
        void get().fetch();
      } else {
        set({ notifications: [], unreadCount: 0 });
      }
    });

    return {
      notifications: [],
      unreadCount: 0,
      isLoading: false,

      fetch: async () => {
        set({ isLoading: true });
        const { data } = await config.list();
        set({ notifications: data?.data ?? [], unreadCount: data?.unreadCount ?? 0, isLoading: false });
      },

      markRead: async (id: string) => {
        const target = get().notifications.find((n) => n.id === id);
        if (!target || target.isRead) return;
        set((state) => ({
          notifications: state.notifications.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
          unreadCount: Math.max(0, state.unreadCount - 1),
        }));
        await config.markRead(id);
      },

      markAllRead: async () => {
        set((state) => ({
          notifications: state.notifications.map((n) => ({ ...n, isRead: true })),
          unreadCount: 0,
        }));
        await config.markAllRead();
      },

      remove: (id: string) => {
        set((state) => {
          const target = state.notifications.find((n) => n.id === id);
          return {
            notifications: state.notifications.filter((n) => n.id !== id),
            unreadCount: Math.max(0, state.unreadCount - (target && !target.isRead ? 1 : 0)),
          };
        });
      },
    };
  });
}

/**
 * The patient/caregiver feed (same account in both portals) — patient socket,
 * patient session, /api/notifications.
 */
export const useNotificationsStore = createNotificationsStore({
  list: listNotificationsApi,
  markRead: markNotificationReadApi,
  markAllRead: markAllNotificationsReadApi,
  onIncoming: (handler) => {
    getSocket().on("notification:new", handler);
  },
  onTokenChange: (handler) => {
    usePatientAuthStore.subscribe((state) => handler(state.accessToken));
  },
  onResync: (handler) => {
    onResync(getSocket(), handler);
  },
});
